// The price list. willow-saas serves it at `GET /v1/prices` (reached here
// through the `/api` proxy); until that answers, the snapshot below (the
// launch table Paul approved "for now" on 10/19) stands in, so plan cards
// always show the approved numbers. Prices are config in saas, so the live
// list wins whenever it loads.

import { useEffect, useState } from 'react';
import { chainName, RPC_CHAINS } from './chains';
import { price, pricePerBlock } from './money';
import type { RpcPlan } from './rpc-client';

export interface ProofPrice {
  chain_id: number;
  chain_name: string;
  micros_per_block: number;
  placeholder?: boolean;
}

export interface PriceList {
  plans: RpcPlan[];
  proofs: { chains: ProofPrice[] };
  credits: {
    min_topup_micros: number | null;
    max_topup_micros: number | null;
    /** Whether agents can top up with USDC over x402 (saas lists it only when it is on). */
    x402: boolean;
  };
  agents: { starter_balance_micros: number | null };
  unit: { unit_bytes: number | null };
  /** Where the numbers came from: the live saas list, or this file. */
  source: 'live' | 'snapshot';
}

const plan = (p: Omit<RpcPlan, 'placeholder'>): RpcPlan => ({ ...p, placeholder: false });

export const PRICE_SNAPSHOT: PriceList = {
  source: 'snapshot',
  plans: [
    plan({
      id: 'free',
      display_name: 'Free',
      monthly_micros: 0,
      included_units: 10_000_000,
      proof_credit_micros: 0,
      requests_per_second: 25,
      unit_price_micros_per_million: null,
      overage_allowed: false,
      proofs: false,
      state_methods: false,
    }),
    plan({
      id: 'payg',
      display_name: 'Pay as you go',
      monthly_micros: 0,
      // None of its own. Free units left in the month of the upgrade stay
      // usable until the month ends (saas `rpc/ledger.rs`).
      included_units: 0,
      proof_credit_micros: 0,
      requests_per_second: 100,
      unit_price_micros_per_million: 2_000_000,
      overage_allowed: false,
      proofs: true,
      state_methods: true,
    }),
    plan({
      id: 'builder',
      display_name: 'Builder',
      monthly_micros: 49_000_000,
      included_units: 50_000_000,
      proof_credit_micros: 10_000_000,
      requests_per_second: 100,
      unit_price_micros_per_million: 1_200_000,
      overage_allowed: true,
      proofs: true,
      state_methods: true,
    }),
    plan({
      id: 'scale',
      display_name: 'Scale',
      monthly_micros: 299_000_000,
      included_units: 400_000_000,
      proof_credit_micros: 50_000_000,
      requests_per_second: 300,
      unit_price_micros_per_million: 900_000,
      overage_allowed: true,
      proofs: true,
      state_methods: true,
    }),
  ],
  proofs: {
    chains: [
      { chain_id: 1, chain_name: 'ethereum', micros_per_block: 20_000 },
      { chain_id: 8453, chain_name: 'base', micros_per_block: 6_000 },
      { chain_id: 42161, chain_name: 'arbitrum-one', micros_per_block: 250 },
      { chain_id: 10, chain_name: 'op', micros_per_block: 6_000 },
      { chain_id: 130, chain_name: 'unichain', micros_per_block: 250 },
      { chain_id: 57073, chain_name: 'ink', micros_per_block: 250 },
      { chain_id: 4663, chain_name: 'robinhood', micros_per_block: 250 },
    ],
  },
  credits: { min_topup_micros: 10_000_000, max_topup_micros: 5_000_000_000, x402: false },
  agents: { starter_balance_micros: 10_000_000 },
  unit: { unit_bytes: 4096 },
};

function isPriceList(v: unknown): v is Omit<PriceList, 'source'> {
  const o = v as Partial<PriceList> | null;
  return (
    !!o &&
    Array.isArray(o.plans) &&
    o.plans.length > 0 &&
    !!o.proofs &&
    Array.isArray(o.proofs.chains)
  );
}

export async function fetchPrices(): Promise<PriceList> {
  try {
    const res = await fetch('/api/v1/prices', { headers: { accept: 'application/json' } });
    if (!res.ok) return PRICE_SNAPSHOT;
    const body: unknown = await res.json();
    if (!isPriceList(body)) return PRICE_SNAPSHOT;
    return {
      plans: body.plans,
      proofs: { chains: body.proofs.chains },
      credits: {
        min_topup_micros: body.credits?.min_topup_micros ?? PRICE_SNAPSHOT.credits.min_topup_micros,
        max_topup_micros: body.credits?.max_topup_micros ?? PRICE_SNAPSHOT.credits.max_topup_micros,
        x402: (body.credits as { x402?: unknown } | undefined)?.x402 != null,
      },
      agents: {
        starter_balance_micros:
          body.agents?.starter_balance_micros ?? PRICE_SNAPSHOT.agents.starter_balance_micros,
      },
      unit: { unit_bytes: body.unit?.unit_bytes ?? PRICE_SNAPSHOT.unit.unit_bytes },
      source: 'live',
    };
  } catch {
    return PRICE_SNAPSHOT;
  }
}

export interface ProofPriceRow {
  chainId: number;
  chain: string;
  perBlock: string;
  perThousandBlocks: string;
}

/** The proof price table, as /pricing and Billing both show it: the docs' chain
 *  order (saas lists them by chain ID), and the same numbers to the digit. */
export function proofPriceRows(prices: PriceList): ProofPriceRow[] {
  const rank = (id: number) => {
    const i = RPC_CHAINS.findIndex((c) => c.id === id);
    return i === -1 ? RPC_CHAINS.length : i;
  };
  return [...prices.proofs.chains]
    .sort((a, z) => rank(a.chain_id) - rank(z.chain_id))
    .map((c) => ({
      chainId: c.chain_id,
      chain: chainName(c.chain_id),
      perBlock: pricePerBlock(c.micros_per_block),
      perThousandBlocks: price(c.micros_per_block * 1000),
    }));
}

/** The price list, starting from the snapshot and replaced by the live list
 *  once it loads. */
export function usePrices(): PriceList {
  const [prices, setPrices] = useState<PriceList>(PRICE_SNAPSHOT);
  useEffect(() => {
    let alive = true;
    fetchPrices().then((p) => {
      if (alive) setPrices(p);
    });
    return () => {
      alive = false;
    };
  }, []);
  return prices;
}
